import React, {useState, useEffect} from "react";
import {getAdminContractorListController, ModifyAdminDashboardContractor, DeleteUsers, ChangeUserStatus, flagContractor} from "../../services/adminServices";
import moment from "moment";
import CustomLoader from "../../CustomComponents/CustomLoader";
import {Autocomplete, TextField, CircularProgress} from "@mui/material";
import {sweetAlertQuestion, sweetAlerts} from "../../utils/sweet-alerts";

const MyContractor = () => {
	const [firstTimeLoad, setFirstTimeLoad] = useState(true);
	const [contractor, setContractor] = useState([]);

	const [loading, setLoading] = useState(true);

	const [filterStatus, setFilterStatus] = useState("all");
	const [sortBy, setSortBy] = useState("name-asc");
	const [filterVerified, setFilterVerified] = useState("all");

	const [showDetailModal, setShowDetailModal] = useState(false);
	const [selectedAdmin, setSelectedAdmin] = useState(null);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showAddModal, setShowAddModal] = useState(false);
	const [editingAdmin, setEditingAdmin] = useState(null);
	const [editFormData, setEditFormData] = useState({});
	const [addFormData, setAddFormData] = useState({
		first_name: "",
		last_name: "",
		email: "",
		password: "",
	});
	const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
	const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);
	const [editErrors, setEditErrors] = useState({});
	const [addErrors, setAddErrors] = useState({});
	const [loadingButtons, setLoadingButtons] = useState({});
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalPages, setTotalPages] = useState(1);
	const [totalLength, setTotalLength] = useState(0);

	const getMockContractor = async () => {
		try {
			if (firstTimeLoad) {
				setLoading(false);
			}

			// Use current page and pageSize instead of hardcoded values
			const response = await getAdminContractorListController({
				page: page,
				pagesize: pageSize,
				sortby: sortBy,
				status: filterStatus,
				verified: filterVerified,
			});

			if (response.status) {
				setTotalCount(response.data.totalCount);
				setContractor(response.data.list);
				setLoading(true);
				setTotalLength(response?.data?.totalCount);
				const totalPages = Math.ceil(response?.data?.totalCount / pageSize);
				setTotalPages(totalPages);
			}
		} catch (error) {
			console.error("Error fetching Contractor:", error);
			setContractor([]);
			setLoading(true);
			// setFilteredContractor([]);
		}
	};

	useEffect(() => {
		getMockContractor();
	}, [pageSize, page]);

	useEffect(() => {
		if (firstTimeLoad) {
			setFirstTimeLoad(false);
		} else {
			setPage(1);
			getMockContractor();
		}
	}, [sortBy, filterStatus, filterVerified]);

	const handleEditInputChange = (field, value) => {
		setEditFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		if (editErrors[field]) {
			setEditErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}
	};

	const handleAddInputChange = (field, value) => {
		setAddFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		if (addErrors[field]) {
			setAddErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}
	};

	const handleViewDetails = (admin) => {
		setSelectedAdmin(admin);
		setShowDetailModal(true);
	};

	const truncateWords = (text, maxChars) => {
		if (text.length <= maxChars) return text;
		return text.substring(0, maxChars) + "...";
	};

	const getStatusBadge = (status) => {
		const config = status ? "Active" : "Inactive";
		return <span className={`badge ${status ? "badge-success" : "badge-warning"}`}>{config}</span>;
	};

	const handleToggleStatus = async (adminId) => {
		setLoadingButtons((prev) => ({...prev, [`toggle-${adminId}`]: true}));

		try {
			const response = await ChangeUserStatus({user_id: adminId});
			if (response.status) {
				await getMockContractor();
				sweetAlerts("success", "Contractor status updated successfully");
				setLoadingButtons((prev) => ({...prev, [`toggle-${adminId}`]: false}));
			} else {
				setLoadingButtons((prev) => ({...prev, [`toggle-${adminId}`]: false}));
				sweetAlerts("error", response?.message || "Error updating contractor status. Please try again.");
			}
		} catch (error) {
			setLoadingButtons((prev) => ({...prev, [`toggle-${adminId}`]: false}));
			sweetAlerts("error", "Error updating contractor status. Please try again.");
		}
	};

	const closeEditModal = () => {
		setShowEditModal(false);
		setEditingAdmin(null);
		setEditFormData({});
		setEditErrors({});
		if (editingAdmin) {
			setLoadingButtons((prev) => ({...prev, [`edit-${editingAdmin.id}`]: false}));
		}
	};

	const openEditModal = (admin) => {
		setEditingAdmin(admin);
		setEditFormData({
			id: admin?.user_id,
			first_name: admin.first_name,
			last_name: admin.last_name,
			email: admin.email,
			role: admin.role,
			status: admin.status,
			business_name: admin?.business_name,
			verified: admin.verified,
		});
		setEditErrors({});
		setShowEditModal(true);
	};

	const validateEditForm = () => {
		const errors = {};

		if (!editFormData.first_name?.trim()) {
			errors.first_name = "First name is required";
		}

		if (!editFormData.last_name?.trim()) {
			errors.last_name = "Last name is required";
		}

		if (!editFormData.email?.trim()) {
			errors.email = "Email is required";
		} else if (!/\S+@\S+\.\S+/.test(editFormData.email)) {
			errors.email = "Email is invalid";
		}

		setEditErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleEditSubmit = async () => {
		if (!validateEditForm()) {
			return;
		}

		sweetAlertQuestion(`Are you sure you want to update this contractor?`).then(async (result) => {
			if (result === "Yes") {
				setIsSubmittingEdit(true);
				setLoadingButtons((prev) => ({...prev, [`edit-${editingAdmin.user_id}`]: true}));

				try {
					const response = await ModifyAdminDashboardContractor({
						first_name: editFormData.first_name,
						last_name: editFormData.last_name,
						email: editingAdmin.email,
						userid: editingAdmin.user_id,
						business_name: editFormData?.business_name,
						verified: editFormData.verified,
					});
					setLoadingButtons((prev) => ({...prev, [`edit-${editingAdmin.user_id}`]: false}));
					if (response.status) {
						sweetAlerts("success", "Contractor updated successfully!");
						await getMockContractor();
						closeEditModal();
						setIsSubmittingEdit(false);
					} else {
						setIsSubmittingEdit(false);
						sweetAlerts("error", response?.message || "Error updating contractor. Please try again.");
					}
				} catch (error) {
					sweetAlerts("error", "Error updating contractor. Please try again.");
					setIsSubmittingEdit(false);
				}
			}
		});
	};

	const handleDeleteAdmin = async (adminId) => {
		sweetAlertQuestion("Are you sure you want to deactivate this contractor?").then(async (result) => {
			if (result === "Yes") {
				setLoadingButtons((prev) => ({...prev, [`delete-${adminId}`]: true}));
				try {
					const response = await DeleteUsers({user_id: adminId});
					if (response.status) {
						// Update total count first
						const newTotalCount = totalCount - 1;
						setTotalCount(newTotalCount);
						setTotalLength(newTotalCount);
						
						// Calculate new total pages
						const newTotalPages = Math.ceil(newTotalCount / pageSize);
						setTotalPages(newTotalPages);
						
						// Check if current page is now empty and not the first page
						if (page > 1 && page > newTotalPages) {
							// Navigate to the previous page
							const newPage = newTotalPages;
							setPage(newPage);
							// Fetch data for the new page
							await getMockContractor();
						} else {
							// Just refresh current page data
							await getMockContractor();
						}
						
						sweetAlerts("success", "Contractor deactivated successfully");
					} else {
						setLoadingButtons((prev) => ({...prev, [`delete-${adminId}`]: false}));
						sweetAlerts("error", response?.message || "Error deleting contractor. Please try again.");
					}
				} catch (error) {
					setLoadingButtons((prev) => ({...prev, [`delete-${adminId}`]: false}));
					sweetAlerts("error", "Error deleting contractor. Please try again.");
				}
			}
		});
	};

	const paginate = (pageNumber) => {
		setPage(pageNumber);
	};

	const handleFlagged = async (adminId) => {
		setLoadingButtons((prev) => ({...prev, [`flag-${adminId}`]: true}));
		try {
			const response = await flagContractor({
				id: adminId,
			});

			if (response && response.status === true) {
				await getMockContractor();
			} else {
				sweetAlerts("error", "Failed flagging contractor. Please try again.");
			}
		} catch (error) {
			sweetAlerts("error", "Error flagging contractor. Please try again.");
		} finally {
			setLoadingButtons((prev) => ({...prev, [`flag-${adminId}`]: false}));
		}
	};

	return (
		<div className="admin-ratings-dashboard-page">
			<div className="page-header d-flex justify-content-between">
				<div className="">
					<h2>Contractor Management</h2>
					<p>View and manage all admin users ({totalLength} total)</p>
				</div>
			</div>

			<div className="ratings-controls">
				<div className="filter-section">
					{/* <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
						<option value="all">All Status</option>
						<option value="active">Active</option>
						<option value="inactive">Inactive</option>
					</select> */}

					<select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
						<option value="all">All</option>
						<option value="flagged">Flagged</option>
						<option value="unflagged">Unflagged</option>
					</select>
					<select className="filter-select" value={filterVerified} onChange={(e) => setFilterVerified(e.target.value)}>
						<option value="all">All Verified</option>
						<option value="verified">Verified</option>
						<option value="unverified">Unverified</option>
					</select>

					<select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
						<option value="created-desc">Newest First</option>
						<option value="created-asc">Oldest First</option>
						<option value="email-asc">Email A-Z</option>
						<option value="email-desc">Email Z-A</option>
					</select>
				</div>
			</div>

			{loading ? (
				<div className="admin-ratings-container">
					{contractor.length === 0 ? (
						<div className="ratings-table-container">
							<div className="table-responsive sticky-table">
								<table className="ratings-table">
									<thead>
										<tr>
											<th className="align-middle address-cell">Contractor Name</th>
											<th className="align-middle address-cell">Contractor Email</th>
											<th className="align-middle address-cell">Verified</th>
											{/* <th className="align-middle address-cell">Badges</th> */}
											<th className="align-middle address-cell">Business</th>
											<th className="align-middle address-cell">Status</th>
											<th className="align-middle address-cell">Created</th>
											<th className="align-middle actions-cell text-end">Actions</th>
										</tr>
									</thead>
									<tbody>
										<tr className="rating-row">
											<td className="address-cell text-start" colSpan={7}>
												No contractor found
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					) : (
						<div className="ratings-table-container">
							<div className="table-responsive sticky-table">
								<table className="ratings-table">
									<thead>
										<tr>
											<th className="align-middle address-cell">Contractor Name</th>
											<th className="align-middle address-cell">Contractor Email</th>
											<th className="align-middle address-cell">Business Verified</th>
											{/* <th className="align-middle address-cell">Badges</th> */}
											<th className="align-middle address-cell">Business</th>
											<th className="align-middle address-cell">Status</th>
											<th className="align-middle address-cell">Created</th>
											<th className="align-middle actions-cell text-end">Actions</th>
										</tr>
									</thead>
									<tbody>
										{contractor.map((admin) => (
											<tr key={admin?.user_id} className={`rating-row ${admin.is_flagged ? "flagged" : ""}`}>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(admin)}>
													{truncateWords(`${admin.first_name} ${admin.last_name}`, 30)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(admin)}>
													{truncateWords(`${admin.email}`, 30)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(admin)}>
													{admin.verified ? (
														<span className="badge badge-success p-2">✓</span>
													) : (
														<span className="badge badge-danger p-2">✗</span>
													)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(admin)}>
													{truncateWords(`${admin?.business_name}`, 30)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(admin)}>
													{getStatusBadge(admin.status)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(admin)}>
													{moment(admin.created_at).format("MMM DD, YYYY")}
												</td>
												<td className="align-middle actions-cell">
													<div className="table-actions mb-0 justify-content-end flex-row">
														<button className="btn-icon" onClick={() => handleFlagged(admin.user_id)} title="Flag Contractor" disabled={loadingButtons[`flag-${admin.user_id}`]}>
															{loadingButtons[`flag-${admin.user_id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
																	<path
																		d="M10.29 3.86L1.82 18C1.64 18.37 1.54 18.78 1.54 19.2C1.54 19.62 1.64 20.03 1.82 20.4C2 20.77 2.26 21.09 2.59 21.33C2.92 21.57 3.31 21.73 3.72 21.8C4.13 21.87 4.55 21.85 4.95 21.75C5.35 21.65 5.72 21.47 6.04 21.22L12 17.27L17.96 21.22C18.28 21.47 18.65 21.65 19.05 21.75C19.45 21.85 19.87 21.87 20.28 21.8C20.69 21.73 21.08 21.57 21.41 21.33C21.74 21.09 22 20.77 22.18 20.4C22.36 20.03 22.46 19.62 22.46 19.2C22.46 18.78 22.36 18.37 22.18 18L13.71 3.86C13.53 3.49 13.27 3.17 12.94 2.93C12.61 2.69 12.22 2.53 11.81 2.46C11.4 2.39 10.98 2.41 10.58 2.51C10.18 2.61 9.81 2.79 9.49 3.04C9.17 3.29 8.91 3.61 8.73 3.98C8.55 4.35 8.45 4.76 8.45 5.18C8.45 5.6 8.55 6.01 8.73 6.38L10.29 3.86Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																	<path d="M12 9V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																	<path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																</svg>
															)}
														</button>
														<button
															className="btn-icon"
															onClick={() => handleToggleStatus(admin?.user_id)}
															title={admin.status === "active" ? "Deactivate" : "Activate"}
															disabled={loadingButtons[`toggle-${admin?.user_id}`]}>
															{loadingButtons[`toggle-${admin?.user_id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																	<path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" stroke="currentColor" strokeWidth="2" />
																</svg>
															)}
														</button>
														<button className="btn-icon" onClick={() => handleViewDetails(admin)} title="View Details">
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" />
																<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
															</svg>
														</button>
														<button className="btn-icon btn-edit" onClick={() => openEditModal(admin)} title="Edit Contractor" disabled={loadingButtons[`edit-${admin?.user_id}`]}>
															{loadingButtons[`edit-${admin?.user_id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path
																		d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																	<path
																		d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																</svg>
															)}
														</button>
														<button className="btn-icon btn-delete" onClick={() => handleDeleteAdmin(admin?.user_id)} title="Deactivate" disabled={loadingButtons[`delete-${admin?.user_id}`]}>
															{loadingButtons[`delete-${admin?.user_id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																	<path
																		d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																</svg>
															)}
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Pagination */}
							<>
								<div className="pagination justify-content-between mt-0 pt-0 pe-3 px-3">
									<div className="pagination-info d-flex justify-content-between align-items-center gap-3">
										<span className="pagination-info-text">
											Showing {page * pageSize - pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
										</span>
										<div>
											<select className="filter-select" value={pageSize} onChange={(e) => setPageSize(e.target.value)}>
												<option value="10">10</option>
												<option value="20">20</option>
												<option value="25">25</option>
												<option value="50">50</option>
												<option value="100">100</option>
											</select>
										</div>
									</div>
									<div className="d-flex justify-content-end pb-3 pt-3 pe-2">
										<button className="pagination-btn" onClick={() => paginate(page - 1)} disabled={page === 1}>
											Previous
										</button>

										<div className="pagination-numbers">
											{[...Array(totalPages)].map((_, index) => {
												const ipage = index + 1;
												if (ipage === 1 || ipage === totalPages || (ipage >= page - 1 && ipage <= page + 1)) {
													return (
														<button key={ipage} className={`pagination-number ${page === ipage ? "active" : ""}`} onClick={() => paginate(ipage)}>
															{ipage}
														</button>
													);
												} else if (ipage === page - 2 || ipage === page + 2) {
													return (
														<span key={ipage} className="pagination-ellipsis">
															...
														</span>
													);
												}
												return null;
											})}
										</div>

										<button className="pagination-btn" onClick={() => paginate(page + 1)} disabled={page === totalPages}>
											Next
										</button>
									</div>
								</div>
							</>
						</div>
					)}
				</div>
			) : (
				<div className="admin-ratings-container">
					<div className="ratings-table-container">
						<div className="table-responsive sticky-table">
							<table className="ratings-table">
								<thead>
									<tr>
										<th className="align-middle address-cell">Contractor Name</th>
										<th className="align-middle address-cell">Contractor Email</th>
										<th className="align-middle address-cell">Business Verified</th>
										<th className="align-middle address-cell">Business</th>
										<th className="align-middle address-cell">Status</th>
										{/* <th className="align-middle address-cell">Created</th> */}
										<th className="align-middle actions-cell text-end">Actions</th>
									</tr>
								</thead>
								<tbody>
									<tr className="rating-row">
										<td className="address-cell text-center" colSpan={6}>
											<CustomLoader />
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}

			{showDetailModal && selectedAdmin && (
				<div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h3>Contractor Details</h3>
							<button className="modal-close" onClick={() => setShowDetailModal(false)}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="detail-section">
								<h4>Contractor Information</h4>
								<div className="detail-grid">
									<div className="detail-item">
										<span className="detail-label">Name:</span>
										<span className="detail-value">
											{selectedAdmin.first_name} {selectedAdmin.last_name}
										</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Email:</span>
										<span className="detail-value">{selectedAdmin.email}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Business:</span>
										<span className="detail-value">{selectedAdmin.business_name}</span>
									</div>

									<div className="detail-item">
										<span className="detail-label">Created:</span>
										<span className="detail-value">{moment(selectedAdmin.created_at).format("MMMM DD, YYYY")}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Status:</span>
										<span className="detail-value">{getStatusBadge(selectedAdmin.status)}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Verified:</span>
										<span className="detail-value">
											{selectedAdmin.verified ? (
												<span className="badge badge-success p-2">✓</span>
											) : (
												<span className="badge badge-danger p-2">✗</span>
											)}
										</span>
									</div>
								</div>
							</div>
						</div>

						<div className="modal-footer">
							<button
								className="btn btn-primary"
								onClick={() => {
									setShowDetailModal(false);
									openEditModal(selectedAdmin);
								}}>
								Edit Contractor
							</button>
							<button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{showEditModal && editingAdmin && (
				<div className="modal-overlay" onClick={closeEditModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: "600px"}}>
						<div className="modal-header">
							<h3>Edit Contractor</h3>
							<button className="modal-close" onClick={closeEditModal}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="form-group">
								<label className="form-label">First Name *</label>
								<input
									type="text"
									className={`form-input ${editErrors.first_name ? "error" : ""}`}
									value={editFormData.first_name || ""}
									onChange={(e) => handleEditInputChange("first_name", e.target.value)}
									placeholder="Enter first name"
								/>
								{editErrors.first_name && <div className="error-message">{editErrors.first_name}</div>}
							</div>

							<div className="form-group">
								<label className="form-label">Last Name *</label>
								<input
									type="text"
									className={`form-input ${editErrors.last_name ? "error" : ""}`}
									value={editFormData.last_name || ""}
									onChange={(e) => handleEditInputChange("last_name", e.target.value)}
									placeholder="Enter last name"
								/>
								{editErrors.last_name && <div className="error-message">{editErrors.last_name}</div>}
							</div>

							<div className="form-group">
								<label className="form-label">Email *</label>
								<input
									type="email"
									className={`form-input   ${editErrors.email ? "error" : ""}`}
									value={editFormData.email || ""}
									onChange={(e) => handleEditInputChange("email", e.target.value)}
									placeholder="Enter email address"
									disabled
								/>
								{editErrors.email && <div className="error-message">{editErrors.email}</div>}
							</div>

							<div className="form-group">
								<label className="form-label">Business *</label>
								<input
									type="text"
									className={`form-input   ${editErrors.business_name ? "error" : ""}`}
									value={editFormData.business_name || ""}
									onChange={(e) => handleEditInputChange("business_name", e.target.value)}
									placeholder="Enter business name"
								/>
								{editErrors.business_name && <div className="error-message">{editErrors.business_name}</div>}
							</div>

							<div className="form-group">
								<label className="form-label"> Business Verified *</label>
								{/* <select className="form-control" onChange={(e) => handleEditInputChange("verified", e.target.value)} disabled={!editFormData.verified }>
									<option value="true" selected={editFormData.verified === true}>Yes</option>
									<option value="false" selected={editFormData.verified === false}>No</option>
								</select> */}
								<select className="form-control" onChange={(e) => handleEditInputChange("verified", e.target.value)} >
									<option value="true" selected={editFormData.verified === true}>Yes</option>
									<option value="false" selected={editFormData.verified === false}>No</option>
								</select>
							</div>
						</div>

						<div className="modal-footer">
							<button className="btn btn-primary" onClick={handleEditSubmit} disabled={isSubmittingEdit}>
								{isSubmittingEdit ? (
									<>
										<span className="spinner-small"></span>
										Updating...
									</>
								) : (
									"Update Contractor"
								)}
							</button>
							<button className="btn btn-secondary" onClick={closeEditModal} disabled={isSubmittingEdit}>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default MyContractor;
